// OPPORTUNITY RADAR (spec §14) and GROWTH ANALYTICS (spec §13).
// Radar: every opportunity in the book with WHY (evidence) and WHAT TO DO (one click). Nothing is sent or
// created automatically — each action opens the normal form so the advisor stays in control.
// Analytics: conversion, average case size, sources and referrals, pipeline value — derived, nothing sampled.
import { useMemo, useState } from 'react';
import { saveCampaign, growthAnalytics, getSetting, addDays, CAMPAIGN_THEMES, themeText, renderTemplate, type SegmentKey } from '@vero/domain';
import { openCustomMessage, templateContext } from '../core/messages.tsx';
import { opportunityRadar, radarSummary, RADAR_KINDS, type RadarItem, type RadarKind } from '@vero/intelligence';
import { useRuntime } from '../core/runtime.tsx';
import { NpsChart } from './nps.tsx';
import { Empty, PageHead, PersonCell, Seg } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { telLink } from '../core/links.ts';
import { openers } from './forms.tsx';

export function RadarView() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const items = useMemo(() => opportunityRadar(view, today), [view, today]);
  const summary = useMemo(() => radarSummary(items), [items]);
  const [kind, setKind] = useState<RadarKind | 'all'>('all');
  const [limit, setLimit] = useState(40);
  const shown = items.filter((x) => kind === 'all' || x.kind === kind);
  const why = (x: RadarItem) => t(x.reasonKey, { ...x.params, ...(x.params.areas ? { areas: String(x.params.areas).split(', ').map((a) => t(`coverage.area.${a}`)).join(', ') } : {}), ...(x.params.event ? { event: i18n.enumLabel('lifeEvent', String(x.params.event)) } : {}), ...(x.params.date ? { date: i18n.date(String(x.params.date)) } : {}) });
  const campaign = async (seg: SegmentKey) => { const cs = await rt.run((v, c) => saveCampaign(v, { title: t(`segment.${seg}`), segmentKey: seg, channel: 'whatsapp', locale: rt.locale.startsWith('el') ? 'el' : 'en', objective: 'new_business' }, c), 'campaigns.created'); if (cs) navigate(`/campaigns/${cs.entityId}`); };
  const act = (x: RadarItem, a: RadarItem['actions'][number]) => {
    const p = view.get('persons', x.personId);
    if (a === 'call') { const href = telLink(p?.primaryPhone); if (href) location.href = href; openers(rt).followup(x.personId, { kind: 'call', title: why(x), dueDate: today }); return; }
    if (a === 'followup') return openers(rt).followup(x.personId, { title: why(x), dueDate: addDays(today, 2) });
    if (a === 'appointment') return openers(rt).appointment(x.personId);
    if (a === 'proposal') return navigate(`/needs/new?person=${x.personId}`);
    if (a === 'campaign' && x.segment) void campaign(x.segment);
    if (a === 'message') return draft(x);
  };
  // idea 45: the polite theme message for this situation as a DRAFT in the composer (never sent automatically)
  const draft = (x: RadarItem) => {
    const theme = CAMPAIGN_THEMES.find((th) => th.segment === x.segment);
    if (!theme) return;
    const tx = themeText(theme, rt.locale);
    const ctx = templateContext(rt, x.personId);
    openCustomMessage(rt, x.personId, { label: tx.title, subject: renderTemplate(tx.emailSubject, ctx).text, body: tx.whatsapp });
  };
  return (
    <div data-testid="radar">
      <PageHead title={t('radar.title')} subtitle={t('radar.subtitle', { count: items.length })} />
      <div className="targets" data-testid="radar-summary">
        {summary.map((s) => (
          <button key={s.kind} className={`target card radar-kpi ${kind === s.kind ? 'on' : ''}`} onClick={() => setKind(kind === s.kind ? 'all' : s.kind)} aria-pressed={kind === s.kind}>
            <span className="upper">{t(`radar.kind.${s.kind}`)}</span><b className="num">{s.count}</b><span className="small muted">{t('radar.book', { amount: i18n.money(s.annual) })}</span>
          </button>))}
      </div>
      <div className="people-toolbar" style={{ margin: '14px 0 0' }}><Seg values={['all', ...RADAR_KINDS.filter((k) => summary.some((s) => s.kind === k))] as const} value={kind} onChange={(x) => setKind(x as RadarKind | 'all')} label={(x) => (x === 'all' ? t('common.all') : t(`radar.kind.${x}`))} testId="radar-filter" /></div>
      <section className="card" style={{ marginTop: 12 }}>
        {shown.length ? <div className="agenda-list">{shown.slice(0, limit).map((x) => (
          <div key={`${x.personId}:${x.kind}:${x.segment ?? ''}:${x.refId ?? ''}`} className="agenda-item radar-row" data-testid="radar-item">
            <div className="grow" style={{ minWidth: 0 }} onClick={() => navigate(personPath(x.personId))} role="button" tabIndex={0}>
              <PersonCell id={x.personId} sub={<><span className="chip teal">{t(`radar.kind.${x.kind}`)}</span> {why(x)}</>} />
            </div>
            <div className="row wrap radar-actions" style={{ gap: 4 }}>
              {x.actions.filter((a) => (a !== 'campaign' && a !== 'message') || x.segment).map((a) => <button key={a} className={`btn btn-sm ${a === x.actions[0] ? 'btn-primary' : ''}`} onClick={() => act(x, a)} data-testid={`radar-${a}`}>{t(`radar.action.${a}`)}</button>)}
            </div>
          </div>))}</div> : <Empty icon="radar">{t('radar.empty')}</Empty>}
        {shown.length > limit && <div style={{ padding: 12, textAlign: 'center' }}><button className="btn btn-sm" onClick={() => setLimit(limit + 40)}>{t('radar.more', { count: shown.length - limit })}</button></div>}
      </section>
      <p className="tiny muted">{t('radar.note')}</p>
    </div>
  );
}

const PERIODS = ['month', 'quarter', 'year', 'last12'] as const;
type Period = typeof PERIODS[number];
function rangeOf(p: Period, today: string): [string, string] {
  const y = today.slice(0, 4), m = Number(today.slice(5, 7));
  if (p === 'month') return [`${today.slice(0, 7)}-01`, today];
  if (p === 'quarter') { const q0 = Math.floor((m - 1) / 3) * 3 + 1; return [`${y}-${String(q0).padStart(2, '0')}-01`, today]; }
  if (p === 'year') return [`${y}-01-01`, today];
  return [addDays(today, -365), today];
}

export function GrowthAnalyticsView() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const [period, setPeriod] = useState<Period>('year');
  const [from, to] = rangeOf(period, today);
  const commission = getSetting<boolean>(view, 'commission.enabled', false);
  const g = useMemo(() => growthAnalytics(view, from, to, today, { commission }), [view, from, to, today, commission]);
  const pct = (x: number | null) => (x == null ? '—' : `${i18n.number(x)}%`);
  const money = (x: number | null) => (x == null ? '—' : i18n.money(x));
  const maxSrc = Math.max(1, ...g.sources.map((s) => s.premium || s.contacts));
  const kpis: [string, string, string?][] = [
    ['growth_a.new_contacts', i18n.number(g.newContacts)], ['growth_a.new_clients', i18n.number(g.newClients)], ['growth_a.conversion', pct(g.conversionRate), 'growth_a.conversion_hint'],
    ['growth_a.issue_rate', pct(g.cases.issueRate), 'growth_a.issue_rate_hint'], ['growth_a.avg_case', money(g.cases.avgIssuedPremium), 'growth_a.avg_case_hint'], ['growth_a.avg_policy', money(g.avgNewPolicyPremium), 'growth_a.avg_policy_hint'],
    ['growth_a.days_to_issue', g.cases.avgDaysToIssue == null ? '—' : i18n.number(g.cases.avgDaysToIssue)], ['growth_a.pipeline', money(g.pipeline.value), 'growth_a.pipeline_hint'], ['growth_a.weighted', money(g.pipeline.weighted), 'growth_a.weighted_hint'],
  ];
  return (
    <div data-testid="growth-analytics">
      <PageHead title={t('growth_a.title')} subtitle={t('growth_a.subtitle', { from: i18n.date(from), to: i18n.date(to) })} actions={<Seg values={PERIODS} value={period} onChange={setPeriod} label={(x) => t(`growth_a.period.${x}`)} testId="ga-period" />} />
      <div className="targets" data-testid="ga-kpis">
        {kpis.map(([k, v, hint]) => <div key={k} className="target card"><span className="upper">{t(k)}</span><b className="num">{v}</b>{hint && <span className="small muted">{t(hint)}</span>}</div>)}
        {g.commission && <div className="target card"><span className="upper">{t('growth_a.commission')}</span><b className="num">{i18n.money(g.commission.earned)}</b><span className="small muted">{t('growth_a.commission_paid', { amount: i18n.money(g.commission.paid) })}</span></div>}
      </div>
      <div className="grid grid-2" style={{ marginTop: 18, alignItems: 'start' }}>
        <section className="card card-pad" data-testid="ga-sources">
          <div className="section-title">{t('growth_a.sources')}</div>
          {g.sources.length ? g.sources.map((s) => (
            <div key={s.source} className="hbar" title={t('growth_a.source_tip', { contacts: s.contacts, clients: s.clients, conversion: pct(s.conversion) })}>
              <span>{t(`growth_a.source.${s.source.replace(/[^a-z0-9_]/g, '_')}`) === `growth_a.source.${s.source.replace(/[^a-z0-9_]/g, '_')}` ? s.label : t(`growth_a.source.${s.source.replace(/[^a-z0-9_]/g, '_')}`)}</span>
              <div><i style={{ width: `${((s.premium || s.contacts) / maxSrc) * 100}%`, background: 'var(--s1)' }} /></div>
              <b className="num">{s.premium ? i18n.money(s.premium) : `${s.contacts}`}<span className="tiny muted"> · {pct(s.conversion)}</span></b>
            </div>)) : <Empty icon="insights">{t('growth_a.no_contacts')}</Empty>}
          <p className="tiny muted" style={{ marginBottom: 0 }}>{t('growth_a.sources_note')}</p>
        </section>
        <section className="card card-pad" data-testid="ga-funnel">
          <div className="section-title">{t('growth_a.cases')}</div>
          <div className="kv">
            <div><span>{t('growth_a.started')}</span><b>{g.cases.started}</b></div>
            <div><span>{t('growth_a.issued')}</span><b>{g.cases.issued}</b></div>
            <div><span>{t('growth_a.declined')}</span><b>{g.cases.declined}</b></div>
            <div><span>{t('growth_a.open_cases')}</span><b>{g.pipeline.cases}</b> · {i18n.money(g.pipeline.casesValue)}</div>
            <div><span>{t('growth_a.open_opps')}</span><b>{g.pipeline.open}</b></div>
            <div><span>{t('growth_a.new_policies')}</span><b>{g.newPolicies}</b></div>
          </div>
          <div className="section-title" style={{ marginTop: 16 }}>{t('growth_a.referrals')}</div>
          <div className="kv">
            <div><span>{t('growth_a.ref_given')}</span><b>{g.referrals.given}</b></div>
            <div><span>{t('growth_a.ref_converted')}</span><b>{g.referrals.converted}</b> · {pct(g.referrals.conversion)}</div>
            <div><span>{t('growth_a.ref_business')}</span><b>{i18n.money(g.referrals.business)}</b></div>
          </div>
          <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn-sm" onClick={() => navigate('/referrals')}><Icon name="people" />{t('nav.referrals')}</button>
            <button className="btn btn-sm" onClick={() => navigate('/pipeline')}><Icon name="flame" />{t('nav.pipeline')}</button>
            <button className="btn btn-sm" onClick={() => navigate('/radar')}><Icon name="radar" />{t('nav.radar')}</button>
          </div>
        </section>
      </div>
      <NpsChart />
      <p className="tiny muted">{t('growth_a.definitions')}</p>
    </div>
  );
}
